import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useUserClients } from '@/hooks/useUserClients';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface NewFormularioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    titulo: string;
    descricao: string;
    categoria: string;
    client_ids: string[];
    pesquisa_satisfacao?: boolean;
    envio_automatico_fechamento?: boolean;
  }) => void;
}

export const NewFormularioDialog = ({ open, onOpenChange, onSubmit }: NewFormularioDialogProps) => {
  const { profile } = useAuth();
  const { clients, isLoading: loadingClients } = useUserClients();
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [pesquisaSatisfacao, setPesquisaSatisfacao] = useState(false);
  const [envioAutomatico, setEnvioAutomatico] = useState(false);

  // Pré-selecionar automaticamente se houver apenas um cliente disponível
  useEffect(() => {
    if (!loadingClients && clients.length === 1 && clientIds.length === 0) {
      setClientIds([clients[0].id]);
    }
  }, [clients, loadingClients, clientIds.length]);

  const toggleClient = (clientId: string) => {
    setClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (clientIds.length === 0) {
      alert('Por favor, selecione pelo menos um cliente responsável');
      return;
    }
    onSubmit({
      titulo,
      descricao,
      categoria,
      client_ids: clientIds,
      pesquisa_satisfacao: pesquisaSatisfacao,
      envio_automatico_fechamento: pesquisaSatisfacao && envioAutomatico,
    });
    setTitulo('');
    setDescricao('');
    setCategoria('');
    setClientIds([]);
    setPesquisaSatisfacao(false);
    setEnvioAutomatico(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Criar Novo Formulário</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="titulo">Título do Formulário *</Label>
            <Input
              id="titulo"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex: Checklist de Manutenção Preventiva"
              required
            />
          </div>

          <div>
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descrição do formulário e instruções de uso"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="categoria">Categoria</Label>
            <Input
              id="categoria"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              placeholder="Ex: Manutenção, Inspeção, Auditoria"
            />
          </div>

          <div>
            <Label>Clientes Responsáveis *</Label>
            {clientIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1 mb-2">
                {clientIds.map(id => {
                  const client = clients.find(c => c.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="gap-1">
                      {client?.name || id}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => toggleClient(id)} />
                    </Badge>
                  );
                })}
              </div>
            )}
            <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
              {loadingClients ? (
                <p className="text-sm text-muted-foreground p-2">Carregando clientes...</p>
              ) : clients.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">Nenhum cliente disponível</p>
              ) : (
                clients.map((client) => (
                  <label
                    key={client.id}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                  >
                    <Checkbox
                      checked={clientIds.includes(client.id)}
                      onCheckedChange={() => toggleClient(client.id)}
                    />
                    <span className="text-sm">{client.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="space-y-4 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Pesquisa de Satisfação</Label>
                <p className="text-sm text-muted-foreground">
                  Marcar este formulário como pesquisa de satisfação pós-atendimento
                </p>
              </div>
              <Switch
                checked={pesquisaSatisfacao}
                onCheckedChange={setPesquisaSatisfacao}
              />
            </div>

            {pesquisaSatisfacao && (
              <div className="flex items-center justify-between pl-4 border-l-2 border-primary/20">
                <div className="space-y-0.5">
                  <Label>Envio automático ao fechar ticket</Label>
                  <p className="text-sm text-muted-foreground">
                    Enviar automaticamente para o solicitante quando o ticket for fechado
                  </p>
                </div>
                <Switch
                  checked={envioAutomatico}
                  onCheckedChange={setEnvioAutomatico}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              Criar e Editar Formulário
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
